/**
 * Full AEAT datos fiscales sign-in wizard for screen_id sign_in_to_aeat.
 * Credentials and AEAT cookies stay on the server; only session id is stored in flow answers.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Screen } from '../../components/flow/GenericScreenRenderer';
import {
  AEAT_METHOD_OPTION_CERTIFICATE,
  AEAT_METHOD_OPTION_CLAVE,
  AEAT_METHOD_OPTION_IBAN,
  AEAT_METHOD_OPTION_REFERENCE,
  AEAT_METHOD_OPTION_NEED_HELP,
  AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN,
  AEAT_SIGN_IN_OPTIONS_FULL,
  aeatSignInCanonicalOptions,
  openAeatAccessHelpInNewTab,
  resolveCanonicalAeatMethod,
  uiLabelToBffFlow,
  withoutAeatCertificateOption,
  AEAT_DEFAULT_PDP,
} from './aeatMethodMapping';
import {
  aeatClavePin,
  aeatClaveResend,
  aeatClaveStart,
  aeatCreateSession,
  aeatFiscalData,
  aeatGetSession,
  aeatRatification,
  aeatReference,
  formatAeatUserFacingError,
  parseFastApiError,
  type FiscalDataResponse,
} from './aeatFiscalApi';
// @ts-ignore — JS module
import { useCertLogin } from './cert_auth/useCertLogin';
import { capture } from '../../shared/posthog';

type Phase =
  | 'form'
  | 'ratification_block'
  | 'cert_fetch'
  | 'clave_identity'
  | 'clave_pin'
  | 'reference_fetch'
  | 'success';

interface AEATSignInWizardProps {
  screen: Screen;
  answers: Record<string, any>;
  onAnswerChange: (questionId: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}

function cleanTitle(title: string): string {
  return title.replace(/\s+/g, ' ').trim();
}

export const AEATSignInWizard: React.FC<AEATSignInWizardProps> = ({
  screen,
  answers,
  onAnswerChange,
  onNext,
  onBack,
  isSubmitting: parentSubmitting,
}) => {
  const methodOptions = useMemo(() => {
    const q0 = screen.questions?.[0] as { options?: string[] } | undefined;
    const opts = q0?.options;
    const raw = Array.isArray(opts) && opts.length > 0 ? opts : [...AEAT_SIGN_IN_OPTIONS_FULL];
    return withoutAeatCertificateOption(raw);
  }, [screen]);

  const canonicalSignInOptions = useMemo(() => aeatSignInCanonicalOptions(), []);

  const [phase, setPhase] = useState<Phase>('form');
  const [method, setMethod] = useState<string>(() =>
    resolveCanonicalAeatMethod(
      String(answers.aeat_method || answers.connection_sign_in_to_aeat || ''),
      methodOptions
    )
  );
  const [nif, setNif] = useState<string>(String(answers.aeat_nif || answers.nif || ''));
  const [ibanLast5, setIbanLast5] = useState<string>(() => {
    const raw = answers.aeat_iban_last5;
    return typeof raw === 'string' ? raw.replace(/\D/g, '').slice(0, 5) : '';
  });
  const ejercicio = useMemo(() => {
    const ty = answers.tax_year;
    if (ty != null && ty !== '') return String(ty).trim();
    const stored = answers.aeat_ejercicio;
    if (stored != null && String(stored).trim() !== '') return String(stored).trim();
    return String(new Date().getFullYear());
  }, [answers.tax_year, answers.aeat_ejercicio]);
  const [aeatSessionId, setAeatSessionId] = useState<string | null>(answers.aeat_session_id || null);
  const [ratificationUrls, setRatificationUrls] = useState<Record<string, string> | null>(null);
  const [claveFecha, setClaveFecha] = useState<string>('');
  const [claveSoporte, setClaveSoporte] = useState<string>('');
  const [claveMovilHint, setClaveMovilHint] = useState<string>('');
  const [clavePin, setClavePin] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [certPassword, setCertPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pinSecondsLeft, setPinSecondsLeft] = useState<number | null>(null);
  const pinTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [prefillSummary, setPrefillSummary] = useState<{ fieldCount: number; hasName: boolean; hasAddress: boolean; hasIncome: boolean; discrepancyCount: number } | null>(null);
  /** Set when fetch succeeds but parsed prefill is missing (parse failure or mock). */
  const [parseIncompleteReason, setParseIncompleteReason] = useState<'mock' | 'parse_failed' | null>(null);

  // ── Ratification auto-polling + popup state ────────────────────────────────
  const [ratPollingActive, setRatPollingActive] = useState(false);
  const [ratificationJustPassed, setRatificationJustPassed] = useState(false);
  const [ratModalOpen, setRatModalOpen] = useState(false);
  const ratDialogRef = useRef<HTMLDivElement | null>(null);
  const ratPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ratPollCountRef = useRef(0);
  const RAT_POLL_INTERVAL_MS = 10_000;
  const RAT_POLL_MAX_ATTEMPTS = 18;

  const fiscalPayload = () => {
    let flowSid: string | undefined;
    try {
      const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('flow_session_id') : null;
      if (stored && stored.trim()) flowSid = stored.trim();
    } catch {
      flowSid = undefined;
    }
    return {
      nif: nif.trim().toUpperCase(),
      pdp: AEAT_DEFAULT_PDP,
      flow_answers: answers && Object.keys(answers).length > 0 ? answers : undefined,
      flow_session_id: flowSid,
    };
  };

  const startPinTimer = useCallback((smsRequestedAt?: string) => {
    if (pinTimerRef.current) clearInterval(pinTimerRef.current);
    const start = smsRequestedAt ? new Date(smsRequestedAt).getTime() : Date.now();
    const PIN_TTL_MS = 180_000; // 3 minutes
    const update = () => {
      const remaining = Math.max(0, Math.ceil((start + PIN_TTL_MS - Date.now()) / 1000));
      setPinSecondsLeft(remaining);
      if (remaining <= 0 && pinTimerRef.current) {
        clearInterval(pinTimerRef.current);
        pinTimerRef.current = null;
      }
    };
    update();
    pinTimerRef.current = setInterval(update, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (pinTimerRef.current) clearInterval(pinTimerRef.current);
    };
  }, []);

  // D6: Session recovery on page refresh
  useEffect(() => {
    const existingId = answers.aeat_session_id;
    if (!existingId || phase !== 'form') return;
    let cancelled = false;
    (async () => {
      try {
        const sess = await aeatGetSession(existingId);
        if (cancelled) return;
        setAeatSessionId(existingId);
        const m = answers.aeat_method || answers.connection_sign_in_to_aeat || '';
        if (m) setMethod(resolveCanonicalAeatMethod(String(m), methodOptions));
        const stepMap: Record<string, Phase> = {
          clave_sms_sent: 'clave_pin',
          clave_pin_ok: 'success',
          clave_www12_ok: 'clave_identity',
          reference_ok: 'success',
          fiscal_fetched: 'success',
        };
        if (sess.flow === 'certificate' && sess.step === 'ratification_checked') {
          setPhase('cert_fetch');
        } else if (stepMap[sess.step]) {
          setPhase(stepMap[sess.step]);
        } else if (sess.step === 'ratification_checked') {
          if (sess.flow === 'clave_movil') setPhase('clave_identity');
          else if (sess.flow === 'reference') setPhase('reference_fetch');
        }
      } catch {
        // session expired or invalid — stay on form
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onAnswerChange('aeat_ejercicio', ejercicio);
  }, [ejercicio]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistCommonAnswers = useCallback(() => {
    onAnswerChange('aeat_method', method);
    onAnswerChange('connection_sign_in_to_aeat', method);
    onAnswerChange('aeat_nif', nif.trim().toUpperCase());
    onAnswerChange('aeat_ejercicio', ejercicio);
    onAnswerChange('aeat_pdp', AEAT_DEFAULT_PDP);
  }, [method, nif, ejercicio, onAnswerChange]);

  const handleFiscalDataError = useCallback((e: unknown) => {
    const p = parseFastApiError(e);
    if (p.code === 'RATIFICATION_REQUIRED_FILE') {
      if (p.ratification_urls && Object.keys(p.ratification_urls).length > 0) {
        setRatificationUrls(p.ratification_urls);
      }
      setPhase('ratification_block');
      setRatModalOpen(true);
      capture('aeat_fiscal_blocked_ratification_5001', { had_urls: Boolean(p.ratification_urls) });
    }
    setError(formatAeatUserFacingError(p));
  }, []);

  const runRatification = useCallback(
    async (sid: string) => {
      const n = nif.trim().toUpperCase();
      const ej = ejercicio;
      return aeatRatification(sid, { nif: n, ejercicio: ej });
    },
    [nif, ejercicio]
  );

  const getRatificationUrlForMethod = useCallback(
    (urls: Record<string, string> | null): string | null => {
      if (!urls) return null;
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) return urls.cert_e || null;
      if (method === AEAT_METHOD_OPTION_CLAVE) return urls.clave_movil || null;
      if (method === AEAT_METHOD_OPTION_REFERENCE) return urls.reference || null;
      return null;
    },
    [method]
  );

  const stopRatPolling = useCallback(() => {
    if (ratPollIntervalRef.current) {
      clearInterval(ratPollIntervalRef.current);
      ratPollIntervalRef.current = null;
    }
    setRatPollingActive(false);
  }, []);

  const proceedAfterRatification = useCallback(async () => {
    if (!aeatSessionId) return;
    try {
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        const res = await aeatFiscalData(aeatSessionId, {
          ...fiscalPayload(),
          p12_password: undefined,
        });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) {
        setRatModalOpen(false);
        setPhase('clave_identity');
      } else {
        setRatModalOpen(false);
        setPhase('reference_fetch');
      }
    } catch (e) {
      handleFiscalDataError(e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aeatSessionId, method]);

  const startRatPolling = useCallback(() => {
    if (!aeatSessionId) return;
    ratPollCountRef.current = 0;
    setRatPollingActive(true);
    setRatificationJustPassed(false);

    ratPollIntervalRef.current = setInterval(async () => {
      ratPollCountRef.current += 1;
      if (ratPollCountRef.current > RAT_POLL_MAX_ATTEMPTS) {
        stopRatPolling();
        return;
      }
      try {
        const rat = await runRatification(aeatSessionId);
        setRatificationUrls(rat.ratification_urls || null);
        if (rat.domicilio_ratificado) {
          stopRatPolling();
          setRatificationJustPassed(true);
          capture('aeat_ratification_auto_synced', {
            session_id: aeatSessionId,
            attempts: ratPollCountRef.current,
          });
        }
      } catch {
        // Polling tolerates transient errors
      }
    }, RAT_POLL_INTERVAL_MS);
  }, [aeatSessionId, runRatification, stopRatPolling]);

  useEffect(() => {
    return () => {
      if (ratPollIntervalRef.current) {
        clearInterval(ratPollIntervalRef.current);
        ratPollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!ratificationJustPassed) return;
    const timeout = setTimeout(() => {
      void proceedAfterRatification();
    }, 1500);
    return () => clearTimeout(timeout);
  }, [ratificationJustPassed, proceedAfterRatification]);

  useEffect(() => {
    if (!ratModalOpen) return;
    ratDialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !ratPollingActive) setRatModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ratModalOpen, ratPollingActive]);

  const handleConnect = async () => {
    setError(null);
    if (!method) {
      setError('Select how you want to sign in to AEAT.');
      return;
    }

    if (method === AEAT_METHOD_OPTION_NEED_HELP) {
      openAeatAccessHelpInNewTab();
      return;
    }

    if (method === AEAT_METHOD_OPTION_UPLOAD_ID_AND_RETURN) {
      onAnswerChange('aeat_method', method);
      onAnswerChange('connection_sign_in_to_aeat', method);
      onAnswerChange('aeat_nif', nif.trim().toUpperCase());
      onAnswerChange('aeat_ejercicio', ejercicio);
      onAnswerChange('aeat_pdp', AEAT_DEFAULT_PDP);
      onNext();
      return;
    }

    if (method === AEAT_METHOD_OPTION_IBAN) {
      const digits = ibanLast5.replace(/\D/g, '').slice(0, 5);
      if (digits.length !== 5) {
        setError('Enter the last 5 digits of your IBAN.');
        return;
      }
      onAnswerChange('aeat_method', method);
      onAnswerChange('connection_sign_in_to_aeat', method);
      onAnswerChange('aeat_nif', nif.trim().toUpperCase());
      onAnswerChange('aeat_ejercicio', ejercicio);
      onAnswerChange('aeat_pdp', AEAT_DEFAULT_PDP);
      onAnswerChange('aeat_iban_last5', digits);
      onNext();
      return;
    }

    if (!nif.trim()) {
      setError('Enter your NIF/NIE.');
      return;
    }

    capture('aeat_connect_clicked', { method_label: method });
    setLoading(true);
    try {
      const flow = uiLabelToBffFlow(method);
      const sid = await aeatCreateSession(flow);
      setAeatSessionId(sid);
      onAnswerChange('aeat_session_id', sid);

      // Certificate method: no file upload — server cert (AEAT_ALLOW_SERVER_CERT_FALLBACK) handles mTLS
      // Clave / Reference: no cert upload needed either

      const rat = await runRatification(sid);
      setRatificationUrls(rat.ratification_urls || null);

      if (!rat.domicilio_ratificado) {
        setPhase('ratification_block');
        setRatModalOpen(true);
        capture('aeat_ratification_blocked', { session_id: sid });
        return;
      }

      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        // Fetch fiscal data directly — no password re-entry needed (server cert handles mTLS)
        const res = await aeatFiscalData(sid, {
          ...fiscalPayload(),
          p12_password: undefined,
        });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) {
        setPhase('clave_identity');
      } else {
        setPhase('reference_fetch');
      }
    } catch (e) {
      const p = parseFastApiError(e);
      setError(formatAeatUserFacingError(p) || 'Connection failed');
      capture('aeat_connect_failed', { code: p.code, message: p.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRetryRatification = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const rat = await runRatification(aeatSessionId);
      setRatificationUrls(rat.ratification_urls || null);
      if (!rat.domicilio_ratificado) {
        setError('Domicilio still not ratified. Complete the process on AEAT and try again.');
        return;
      }
      if (method === AEAT_METHOD_OPTION_CERTIFICATE) {
        const res = await aeatFiscalData(aeatSessionId, {
          ...fiscalPayload(),
          p12_password: undefined,
        });
        finishSuccess('certificate', res);
      } else if (method === AEAT_METHOD_OPTION_CLAVE) setPhase('clave_identity');
      else setPhase('reference_fetch');
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFiscalCert = async () => {
    if (!aeatSessionId) return;
    if (!certPassword.trim()) {
      setError('Enter the PKCS#12 password to decrypt your certificate for this request.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await aeatFiscalData(aeatSessionId, {
        ...fiscalPayload(),
        p12_password: certPassword,
      });
      finishSuccess('certificate', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleClaveRequestSms = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await aeatClaveStart(aeatSessionId, {
        nif: nif.trim().toUpperCase(),
        fecha: claveFecha.trim() || undefined,
        soporte: claveSoporte.trim() || undefined,
      });
      setClaveMovilHint(res.movil || '');
      setPhase('clave_pin');
      startPinTimer((res as any).sms_requested_at);
      capture('aeat_clave_sms_sent', { mock: res.mock });
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleClaveResendSms = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await aeatClaveResend(aeatSessionId);
      setClaveMovilHint(res.movil || '');
      setClavePin('');
      startPinTimer(res.sms_requested_at);
      capture('aeat_clave_sms_resent', { resend_count: res.resend_count });
    } catch (e) {
      setError(formatAeatUserFacingError(parseFastApiError(e)));
    } finally {
      setLoading(false);
    }
  };

  const handleClaveVerifyAndFetch = async () => {
    if (!aeatSessionId) return;
    setError(null);
    setLoading(true);
    try {
      await aeatClavePin(aeatSessionId, clavePin.trim());
      const res = await aeatFiscalData(aeatSessionId, fiscalPayload());
      finishSuccess('clave_movil', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleReferenceAndFetch = async () => {
    if (!aeatSessionId) return;
    if (reference.trim().length !== 6) {
      setError('Enter the 6-character reference.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await aeatReference(aeatSessionId, {
        nif: nif.trim().toUpperCase(),
        reference: reference.trim().toUpperCase(),
      });
      const res = await aeatFiscalData(aeatSessionId, fiscalPayload());
      finishSuccess('reference', res);
    } catch (e) {
      handleFiscalDataError(e);
    } finally {
      setLoading(false);
    }
  };

  const finishSuccess = (kind: string, res: FiscalDataResponse) => {
    const parsed = res.parsed;
    persistCommonAnswers();
    onAnswerChange('aeat_connection_completed', true);
    onAnswerChange('aeat_fiscal_data_received', true);
    onAnswerChange('aeat_flow_kind', kind);

    if (!parsed?.prefill_answers) {
      setParseIncompleteReason(res.mock ? 'mock' : 'parse_failed');
      setPrefillSummary(null);
    } else {
      setParseIncompleteReason(null);
    }

    if (parsed?.prefill_answers) {
      const prefillEntries = Object.entries(parsed.prefill_answers);
      for (const [key, value] of prefillEntries) {
        if (value !== null && value !== '' && value !== undefined) {
          const prov = parsed.provenance?.[key];
          if (prov?.source === 'aeat_conflicted') {
            continue;
          }
          onAnswerChange(key, value);
        }
      }
      if (parsed.provenance) {
        onAnswerChange('aeat_import_provenance', parsed.provenance);
      }
      onAnswerChange('aeat_import_original_values', parsed.prefill_answers);
      if (parsed.metadata) {
        onAnswerChange('aeat_import_metadata', parsed.metadata);
      }
      if (parsed.discrepancies && parsed.discrepancies.length > 0) {
        onAnswerChange('aeat_import_discrepancies', parsed.discrepancies);
      }
      setPrefillSummary({
        fieldCount: parsed.field_count || prefillEntries.length,
        hasName: Boolean(parsed.taxpayer?.first_name),
        hasAddress: Boolean(parsed.address?.street),
        hasIncome: (parsed.income_records?.length ?? 0) > 0,
        discrepancyCount: parsed.discrepancies?.length ?? 0,
      });
      capture('aeat_prefill_applied', {
        kind,
        field_count: parsed.field_count,
        has_taxpayer: Boolean(parsed.taxpayer),
        has_address: Boolean(parsed.address),
        income_count: parsed.income_records?.length ?? 0,
        discrepancy_count: parsed.discrepancies?.length ?? 0,
      });
    }

    setPhase('success');
    capture('aeat_fiscal_success', { kind, had_parsed_prefill: Boolean(parsed?.prefill_answers) });
  };

  const handleFinalContinue = () => {
    persistCommonAnswers();
    onNext();
  };

  const busy = loading || parentSubmitting;

  const { login: certLogin, isLoading: certLoading, error: certError, isAuthenticated: certAuthenticated, nif: certNif, fullName: certFullName } = useCertLogin();

  const handleMethodClick = async (canonical: string) => {
    if (canonical === AEAT_METHOD_OPTION_NEED_HELP) {
      openAeatAccessHelpInNewTab();
      return;
    }
    setMethod(canonical);
    if (canonical === AEAT_METHOD_OPTION_IBAN) {
      const raw = answers.aeat_iban_last5;
      setIbanLast5(typeof raw === 'string' ? raw.replace(/\D/g, '').slice(0, 5) : '');
    } else {
      setIbanLast5('');
    }
    if (canonical === AEAT_METHOD_OPTION_CERTIFICATE) {
      try {
        const result = await certLogin() as { nif?: string } | null;
        if (result?.nif) {
          setNif(result.nif);
          onAnswerChange('aeat_nif', result.nif);
        }
      } catch {
        // error surfaced via certError from the hook
      }
    }
  };

  return (
    <div className="bg-gray-50" style={{ overflowAnchor: 'none' } as React.CSSProperties}>
      <div className="flex items-start justify-center p-8">
        <div className="max-w-lg w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            {cleanTitle(screen.title)}
          </h1>

          {phase === 'form' && (
            <>
              <p className="text-sm text-gray-600 mb-4 text-center">
                We&apos;ll import your tax data automatically — saving you time and reducing errors. You log in on
                the Tax Agency&apos;s secure website; InnoTaxes never sees your username or password. We do not store
                your Cl@ve PIN or reference on this device.
              </p>

              <p className="text-gray-700 font-medium mb-3">
                How would you like to connect with the Spanish Tax Agency?
              </p>
              <div className="space-y-3 mb-6">
                {methodOptions.map((displayLabel, index) => {
                  const canonical = canonicalSignInOptions[index];
                  if (!canonical) return null;
                  return (
                    <label
                      key={`aeat-method-${index}-${canonical}`}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        method === canonical ? 'border-[#07A2A0] bg-[#07A2A0]/5' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="aeat_method"
                        checked={method === canonical}
                        onChange={() => void handleMethodClick(canonical)}
                        className="sr-only"
                      />
                      <span className="text-gray-900">{displayLabel}</span>
                    </label>
                  );
                })}
              </div>

              {method === AEAT_METHOD_OPTION_IBAN ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last 5 digits of your IBAN</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={5}
                    value={ibanLast5}
                    onChange={(e) => setIbanLast5(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              ) : null}

              <label className="block text-sm font-medium text-gray-700 mb-1">NIF / NIE</label>
              <input
                type="text"
                value={nif}
                onChange={(e) => setNif(e.target.value)}
                placeholder=""
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                autoComplete="off"
              />

              {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}

              <div className="flex gap-4">
                {screen.navigation?.can_go_back && (
                  <button
                    type="button"
                    onClick={onBack}
                    disabled={busy}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={busy}
                  className="flex-1 px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg disabled:opacity-50"
                >
                  {busy ? 'Connecting…' : 'Connect to AEAT'}
                </button>
              </div>
            </>
          )}

          {phase === 'ratification_block' && (
            <div>
              {ratificationJustPassed ? (
                <p className="text-green-800 bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  Fiscal domicile ratified. Fetching your tax data...
                </p>
              ) : (
                <>
                  <p className="text-gray-800 mb-4">
                    Your fiscal domicile must be ratified with AEAT before we can download tax data. Complete
                    ratification on AEAT, then return here.
                  </p>

                  {ratPollingActive && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <svg className="h-4 w-4 animate-spin text-[#07A2A0]" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Checking automatically...
                    </div>
                  )}

                  {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => { stopRatPolling(); setPhase('form'); }}
                      className="px-6 py-3 border-2 border-gray-300 rounded-lg"
                    >
                      Edit details
                    </button>
                    <button
                      type="button"
                      onClick={() => setRatModalOpen(true)}
                      className="flex-1 px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg"
                    >
                      View ratification steps
                    </button>
                  </div>
                  {!ratPollingActive && (
                    <button
                      type="button"
                      onClick={handleRetryRatification}
                      disabled={busy}
                      className="mt-3 w-full px-6 py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-lg"
                    >
                      {busy ? 'Checking...' : 'Check again'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {phase === 'cert_fetch' && (
            <div>
              <p className="text-gray-700 mb-3">
                Enter your PKCS#12 password again to unlock the certificate for this request (the password is not
                stored on the server).
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">PKCS#12 password</label>
              <input
                type="password"
                autoComplete="off"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                value={certPassword}
                onChange={(e) => setCertPassword(e.target.value)}
              />
              {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}
              <button
                type="button"
                onClick={handleFetchFiscalCert}
                disabled={busy}
                className="w-full px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg mb-4"
              >
                {busy ? 'Requesting…' : 'Fetch fiscal data'}
              </button>
              <button type="button" onClick={() => setPhase('form')} className="text-sm text-gray-600 underline">
                Back to form
              </button>
            </div>
          )}

          {phase === 'clave_identity' && (
            <div>
              <p className="text-gray-700 mb-3">
                NIE (NIF starting with a letter): fill Soporte (número de soporte on the card) only and leave the
                expiry date empty. DNI: fill the expiry date (DD-MM-AAAA or DD/MM/AAAA) and leave Soporte empty. Use
                the same tax year (ejercicio) as on AEAT.
              </p>
              <label className="block text-sm font-medium mb-1">Expiry date (DNI)</label>
              <input
                className="w-full border rounded-lg px-3 py-2 mb-3"
                value={claveFecha}
                onChange={(e) => setClaveFecha(e.target.value)}
                placeholder="DD-MM-AAAA"
              />
              <label className="block text-sm font-medium mb-1">Soporte (NIE)</label>
              <input
                className="w-full border rounded-lg px-3 py-2 mb-4"
                value={claveSoporte}
                onChange={(e) => setClaveSoporte(e.target.value)}
              />
              {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}
              <button
                type="button"
                onClick={handleClaveRequestSms}
                disabled={busy}
                className="w-full px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg"
              >
                {busy ? 'Sending…' : 'Request SMS code'}
              </button>
            </div>
          )}

          {phase === 'clave_pin' && (
            <div>
              {claveMovilHint && (
                <p className="text-gray-700 mb-2">Code sent to mobile ending in {claveMovilHint}</p>
              )}
              {pinSecondsLeft !== null && (
                <p className={`text-sm mb-2 ${pinSecondsLeft > 0 ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
                  {pinSecondsLeft > 0
                    ? `PIN expires in ${Math.floor(pinSecondsLeft / 60)}:${String(pinSecondsLeft % 60).padStart(2, '0')}`
                    : 'PIN expired — request a new one'}
                </p>
              )}
              <label className="block text-sm font-medium mb-1">SMS PIN</label>
              <input
                type="password"
                className="w-full border rounded-lg px-3 py-2 mb-4"
                value={clavePin}
                onChange={(e) => setClavePin(e.target.value)}
                autoComplete="one-time-code"
              />
              {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}
              <button
                type="button"
                onClick={handleClaveVerifyAndFetch}
                disabled={busy || pinSecondsLeft === 0}
                className="w-full px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg mb-3"
              >
                {busy ? 'Verifying…' : 'Verify PIN and fetch fiscal data'}
              </button>
              <button
                type="button"
                onClick={handleClaveResendSms}
                disabled={busy}
                className="w-full text-sm text-[#07A2A0] underline"
              >
                Resend SMS code
              </button>
            </div>
          )}

          {phase === 'reference_fetch' && (
            <div>
              <label className="block text-sm font-medium mb-1">Número de referencia (6 alphanumeric characters)</label>
              <input
                className="w-full border rounded-lg px-3 py-2 mb-1 uppercase font-mono tracking-widest"
                value={reference}
                maxLength={6}
                pattern="[A-Z0-9]{6}"
                placeholder="e.g. AB12C3"
                onChange={(e) => setReference(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
              />
              <p className="text-xs text-gray-500 mb-3">Letters A-Z and digits 0-9 only</p>
              {error && <p className="text-red-600 text-sm mb-3 whitespace-pre-line">{error}</p>}
              <button
                type="button"
                onClick={handleReferenceAndFetch}
                disabled={busy}
                className="w-full px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg"
              >
                {busy ? 'Validating…' : 'Validate reference and fetch fiscal data'}
              </button>
            </div>
          )}

          {phase === 'success' && (
            <div>
              <p className="text-green-800 bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                AEAT connection completed. Fiscal data was requested successfully.
              </p>
              {parseIncompleteReason === 'parse_failed' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-amber-950 text-sm">
                  <p className="font-medium mb-1">Automatic import incomplete</p>
                  <p>
                    We received a response from AEAT but could not map it into questionnaire fields (unexpected format
                    or parse error). Your answers were not pre-filled; you can still continue and enter details manually.
                  </p>
                </div>
              )}
              {parseIncompleteReason === 'mock' && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-slate-800 text-sm">
                  Mock fiscal mode: prefill from the downloaded file is disabled. Continue to enter data manually.
                </div>
              )}
              {prefillSummary && prefillSummary.fieldCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-900 font-medium mb-2">
                    {prefillSummary.fieldCount} fields imported from AEAT
                  </p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {prefillSummary.hasName && <li>Identity and personal details</li>}
                    {prefillSummary.hasAddress && <li>Fiscal address</li>}
                    {prefillSummary.hasIncome && <li>Employment income and withholdings</li>}
                  </ul>
                  <p className="text-xs text-blue-600 mt-2">
                    Imported values will appear pre-filled in the questionnaire. You can review and change them on each screen.
                  </p>
                </div>
              )}
              {prefillSummary && prefillSummary.discrepancyCount > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
                  <p className="text-amber-900 font-medium mb-1">
                    {prefillSummary.discrepancyCount} field{prefillSummary.discrepancyCount > 1 ? 's' : ''} differ from your previous answers
                  </p>
                  <p className="text-xs text-amber-700">
                    AEAT returned values that differ from what you entered earlier. Your existing answers have been kept.
                    Please review these fields as you continue through the questionnaire.
                  </p>
                </div>
              )}
              <div className="flex gap-4">
                {screen.navigation?.can_go_back && (
                  <button type="button" onClick={onBack} className="px-6 py-3 border-2 border-gray-300 rounded-lg">
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleFinalContinue}
                  className="flex-1 px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {ratModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => { if (!ratPollingActive) setRatModalOpen(false); }}
          role="presentation"
        >
          <div
            ref={ratDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="aeat-rat-title"
            tabIndex={-1}
            className="w-full max-w-[450px] rounded-2xl bg-white p-6 shadow-lg outline-none focus:ring-2 focus:ring-[#07A2A0]/40"
            onClick={(e) => e.stopPropagation()}
          >
            {ratificationJustPassed ? (
              <div className="flex flex-col gap-4 items-center">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-base font-medium text-center text-gray-900 leading-snug">
                  Fiscal domicile ratified
                </h2>
                <p className="text-sm text-center text-gray-600">
                  Your address has been confirmed with AEAT. Proceeding to fetch your fiscal data...
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <h2 id="aeat-rat-title" className="text-base font-medium text-center text-gray-900 leading-snug">
                  One quick step on the Tax Agency website
                </h2>
                <div className="flex flex-col gap-3 text-sm leading-6 text-gray-600">
                  <p className="text-center">
                    To access your tax data, Spain's Tax Agency (AEAT) requires you to confirm your registered address.
                    It only takes a few seconds.
                  </p>
                  <div className="border border-gray-200"></div>
                  <p className="font-normal text-xs text-gray-500">Once on the AEAT website:</p>
                  <ol className="list-decimal list-inside flex flex-col gap-2 pl-1 text-xs text-gray-500">
                    <li>Check that your address is correct</li>
                    <li>Select "Titularidad"</li>
                    <li>Tap "Rectificar"</li>
                  </ol>
                </div>

                {ratPollingActive ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <svg className="h-4 w-4 animate-spin text-[#07A2A0]" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Checking if ratification is complete...
                    </div>
                    <p className="text-xs text-center text-gray-500">
                      Complete the steps on the AEAT website and we'll detect it automatically.
                    </p>
                  </div>
                ) : !ratPollingActive && ratPollCountRef.current >= RAT_POLL_MAX_ATTEMPTS ? (
                  <div className="flex flex-col gap-3">
                    <p className="text-xs text-center text-amber-700">
                      Automatic check timed out. Complete ratification on AEAT, then check manually.
                    </p>
                    <button
                      type="button"
                      className="w-full px-6 py-3 bg-[#07A2A0] text-white font-semibold rounded-lg disabled:opacity-50"
                      disabled={busy}
                      onClick={() => {
                        setRatModalOpen(false);
                        void handleRetryRatification();
                      }}
                    >
                      {busy ? 'Checking...' : 'Check again'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-full px-6 py-3 bg-[#6ECFCF] text-gray-900 font-semibold rounded-lg hover:bg-[#069694] transition-colors disabled:opacity-50"
                    disabled={busy}
                    onClick={() => {
                      const url = getRatificationUrlForMethod(ratificationUrls);
                      if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      startRatPolling();
                    }}
                  >
                    Take me to AEAT
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AEATSignInWizard;
